/**
 * Phone-width side menu for screens that have a left rail (Shop, Settings, Admin).
 *
 * Below the `sm` breakpoint the rail collapses behind a burger button that sits next to a chip naming the current
 * page; tapping it slides the rail in as a panel, and picking an entry closes it again. From `sm` up the wrapper is
 * `display: contents` and the bar is hidden, so the desktop layout is exactly what it was.
 *
 * Markup contract (see `sideMenuBarHtml`): a `[data-side-menu]` wrapper holding the bar and the `<nav>` (which
 * carries `side-menu-panel`). Open state is the wrapper's `data-open` attribute — CSS does the animation — so a
 * screen that re-renders its markup simply comes back closed.
 */

/** Opening tag of the wrapper. Close it with `</div>` after the nav. */
export const SIDE_MENU_OPEN = '<div class="side-menu-wrap relative sm:contents" data-side-menu data-open="false">';

export function sideMenuBarHtml(chipLabel: string, menuLabel: string, navId: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  return `
    <div class="sm:hidden flex items-center gap-2" data-side-menu-bar>
      <button type="button" data-side-menu-toggle aria-expanded="false" aria-controls="${navId}" aria-label="${esc(menuLabel)}"
        class="side-menu-burger shrink-0 w-11 h-11 rounded border border-border bg-surface-2 text-text touch-manipulation">
        <span class="side-menu-burger-lines" aria-hidden="true"><i></i><i></i><i></i></span>
      </button>
      <span data-side-menu-chip class="side-menu-chip min-h-11 inline-flex items-center px-4 text-[11px] uppercase tracking-[0.14em] text-text">${esc(chipLabel)}</span>
    </div>`;
}

export function setSideMenuChip(root: ParentNode, label: string): void {
  const chip = root.querySelector('[data-side-menu-chip]');
  if (chip) chip.textContent = label;
}

function setOpen(wrap: HTMLElement, open: boolean): void {
  wrap.dataset.open = String(open);
  wrap.querySelector('[data-side-menu-toggle]')?.setAttribute('aria-expanded', String(open));
}

/** Delegated wiring on a stable root. Returns teardown. */
export function mountSideMenu(root: HTMLElement): () => void {
  const onClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    const wrap = target.closest<HTMLElement>('[data-side-menu]');
    if (wrap && target.closest('[data-side-menu-toggle]')) {
      setOpen(wrap, wrap.dataset.open !== 'true');
      return;
    }
    // Picking an entry closes the panel; the screen's own handler switches the page.
    if (wrap && target.closest('.side-menu-panel button')) {
      setOpen(wrap, false);
      return;
    }
    // Tap outside closes any open menu.
    root.querySelectorAll<HTMLElement>('[data-side-menu][data-open="true"]').forEach((w) => setOpen(w, false));
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    root.querySelectorAll<HTMLElement>('[data-side-menu][data-open="true"]').forEach((w) => {
      setOpen(w, false);
      w.querySelector<HTMLElement>('[data-side-menu-toggle]')?.focus();
    });
  };
  root.addEventListener('click', onClick);
  root.addEventListener('keydown', onKey);
  return () => {
    root.removeEventListener('click', onClick);
    root.removeEventListener('keydown', onKey);
  };
}
